const {
    Matrix4,
    Vector3
} = THREE;
import controller from './controller'
import Vue from 'vue';

const ChainState = {
    EMPTY: "Empty Chain",
    MISSING_MODEL: "Missing Model",
    READY_TO_RUN: "Ready",
};

const store = {
    namespaced: true,

    state: {
        chains: []
    },

    mutations: {
        ADD_CHAIN(state, data) {
            let newChain = {
                id: data.id,
                name: data.name,
                state: ChainState.EMPTY,
                usedModels: [],
                usedVolumes: [],
                selectedParentNode: null,
                nodes: [{
                    id: generateUUID(),
                    volumes: "",
                    children: []
                }],
                joints: []
            };

            newChain.selectedParentNode = newChain.nodes[0];

            state.chains.push(newChain);
        },

        UPDATE_CHAIN_STATE(state, event) {
            for (let chain of state.chains) {
                if (!chain.usedModels.includes(event.modelId) || chain.usedModels.length === 0) {
                    continue;
                }

                if (event.type === ModelEvents.UNLOAD) {
                    chain.state = ChainState.MISSING_MODEL;
                } else {
                    if (chain.usedModels.filter(model => !modelManager.getModelById(model)).length > 0) {
                        chain.state = ChainState.MISSING_MODEL;
                    } else {
                        chain.state = ChainState.READY_TO_RUN;
                    }
                }
            }
        },

        DELETE_CHAIN(state, data) {
            state.chains.splice(state.chains.indexOf(data), 1);
        },

        DELETE_ALL_CHAINS(state) {
            state.chains.splice(0, state.chains.length);
        },

        ADD_JOINT(state, data) {
            let chain = state.chains.find(chain => chain.id === data.id);
            chain.nodes.push({
                id: data.joint.id || generateUUID(),
                volumes: data.joint.volumes.slice(),
                children: []
            });

            chain.selectedParentNode.children.push({
                id: data.joint.id || generateUUID(),
                nodeID: data.joint.nodeID || chain.nodes[chain.nodes.length - 1].id,
                volumes: data.joint.volumes.slice(),
                usedModels: data.joint.usedModels.slice(),
                type: data.joint.type,
                angle: data.joint.angle,
                sliderLength: data.joint.sliderLength,
                hingeAxis: new Vector3().copy(data.joint.hingeAxis),
                pointOfAction: new Vector3().copy(data.joint.pointOfAction),
                pointOfControl: new Vector3().copy(data.joint.pointOfControl),
                initialDirection: new Vector3().copy(data.joint.initialDirection),
                useCustomBone: data.joint.useCustomBone,
                currentPointMatrix: new Matrix4().copy(data.joint.currentPointMatrix),
            });

            let models = data.joint.volumes.map(volume => getModelIdFromBranchId(volume));
            let missingModelCount = models.filter(model => !model).length;
            models = models.filter(model => !!model);

            if (missingModelCount > 0 && data.joint.usedModels.length > 0) {
                models.push(...data.joint.usedModels);
                chain.state = ChainState.MISSING_MODEL;
            } else if (missingModelCount === 0 && chain.state !== ChainState.MISSING_MODEL) {
                chain.state = ChainState.READY_TO_RUN;
            }

            for (let model of models) {
                if (!chain.usedModels.includes(model)) {
                    chain.usedModels.push(model);
                }
            }

            for (let volume of data.joint.volumes) {
                chain.usedVolumes.push(volume);
            }

            if (!data.retainParentJoint) {
                chain.selectedParentNode = chain.nodes[chain.nodes.length - 1];
            }
        },

        DELETE_JOINT(state, data) {
            let chain = state.chains.find(chain => chain.id === data.id);
            let index = chain.nodes.findIndex(node => node.children[0] === data.joint);
            let deletedModelIds = data.joint.volumes.map(volume => getModelIdFromBranchId(volume));

            if (index !== -1) {
                if (chain.selectedParentNode === chain.nodes[index + 1]) {
                    chain.selectedParentNode = chain.nodes[index];
                }

                chain.nodes[index].children.pop();

                if (chain.nodes[index + 1].children.length > 0) {
                    chain.nodes[index].children.push(chain.nodes[index + 1].children.pop());
                }

                chain.nodes.splice(index + 1, 1);
            }

            let joints = chain.nodes.map(node => node.children[0]).filter(joint => !!joint);

            for (let joint of joints) {
                let models = joint.volumes.map(volume => getModelIdFromBranchId(volume));
                deletedModelIds = deletedModelIds.filter(model => !models.includes(model));

                if (deletedModelIds.length === 0) {
                    break;
                }
            }
            deletedModelIds.forEach(model => chain.usedModels.splice(chain.usedModels.indexOf(model), 1));

            if (joints.length === 0) {
                chain.state = ChainState.EMPTY;
            }
        }
    },

    actions: {
        reset({commit}) {
            commit('RESET_CURRENT_JOINT');
        },

        populateChains({commit}, data) {
            commit('DELETE_ALL_CHAINS');

            for (let chain of data) {
                commit('ADD_CHAIN', {
                    id: chain._id,
                    name: chain.name
                });

                for (let node of chain.nodes) {
                    if (node.children.length > 0) {
                        commit('ADD_JOINT', {
                            id: chain._id,
                            joint: node.children[0]
                        });
                    }
                }
            }
        },

        async importChain({dispatch, commit}, chain) {
            let newID = await dispatch('addChain', {
                name: chain.name
            });

            for (let node of chain.nodes) {
                if (node.children.length > 0) {
                    dispatch('addJointToChain', {
                        id: newID,
                        joint: node.children[0]
                    });
                }
            }
        },

        async addChain({commit, state, dispatch}, data) {
            let newID;

            try {
                newID = (await KinematicsService.create({
                    name: data.name,
                    meetingID: getMeetingIdFromURL()
                }))._id;
            } catch (e) {
                VRMessageController.addMessage({
                    text: "A chain of that name already exists",
                    priority: VrMessagePriorityEnum.ERROR
                });
                return;
            }

            commit('ADD_CHAIN', {
                id: newID,
                name: data.name
            });

            dispatch('updateChainInDB', newID);

            return newID;
        },

        async deleteChain({commit}, data) {
            commit('DELETE_CHAIN', data);

            await KinematicsService.delete({
                id: data.id
            });
        },

        async addJointToChain({commit, state, dispatch}, data) {
            commit('ADD_JOINT', {
                id: data.id,
                joint: data.joint
            });

            dispatch('updateChainInDB', data.id);
        },

        async deleteJointFromChain({commit, state, dispatch}, data) {
            commit('DELETE_JOINT', data);

            dispatch('updateChainInDB', data.id);
        },

        async updateChainInDB({state}, chainID) {
            let chain = state.chains.find(chain => chain.id === chainID);

            await KinematicsService.update({
                id: chainID,
                cacheFiles: chain.usedModels,
                nodes: chain.nodes
            });
        },

        start({state}, chainID) {
            let nodes = state.chains.find(chain => chain.id === chainID).nodes;

            controller.parseNodes(nodes);

            // for (let joint of nodes) {
            //     controller.parseJoint(joint);
            // }

            controller.startProcess();
            return controller.active;
        },

        stop({state}) {
            controller.jointsArray.splice(0, controller.jointsArray.length);

            controller.endProcess();
            return controller.active;
        },

        runDemo({state}, data) {
            let joint = {
                volumes: data.volumes.slice(),
                type: data.type,
                angle: data.angle,
                sliderLength: data.sliderLength,
                hingeAxis: data.hingeAxis.clone(),
                pointOfAction: data.pointOfAction.clone(),
                pointOfControl: data.pointOfControl.clone(),
                initialDirection: data.initialDirection.clone(),
                useCustomBone: data.useCustomBone,
                currentPointMatrix: data.currentPointMatrix.clone()
            };

            controller.startForSingleJoint(joint);
        },

        stopDemo() {
            controller.endFunction();
        }
    }
};


export default function register(rootStore) {
    rootStore.registerModule('kinematics', store);

    wv.rootObject.addEventListener(ModelEvents.LOAD, event => {
        rootStore.commit('kinematics/UPDATE_CHAIN_STATE', event);
    });

    wv.rootObject.addEventListener(ModelEvents.UNLOAD, event => {
        rootStore.commit('kinematics/UPDATE_CHAIN_STATE', event);
    });
}
